import React, { FC, useRef, useState } from 'react';
import { Button, Modal, Tooltip } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { BiMessageSquareEdit } from 'react-icons/bi';
import { BsHandThumbsDown, BsHandThumbsUp } from 'react-icons/bs';
import { SegmentationLabel } from 'models/segmentation';
import { Decision } from 'models/reviews';
import { AdvancedFilterValue } from 'models/advancedFilters';
import { COLORS } from 'theme';
import { FrameImage } from '../styled';
import SegICIDOverlay from './SegICIDOveraly';
import './styles.css';

interface Props {
  open: boolean;
  onClose: () => void;
  imageSrc: string;
  frameIndex: number;
  allSegmentations: SegmentationLabel[];
  visibleSegmentations: Set<string>;
  onSegmentationToggle: (segmentationId: string, visible: boolean) => void;
  isSegmentationAvailable: (segmentation: SegmentationLabel) => boolean;
  segmentationClassOptions: AdvancedFilterValue[];
  getLocalDecision?: (id: string) => Decision | undefined;
  removedReviews?: Record<string, boolean>;
  onEditSegmentation: (segmentation: SegmentationLabel) => void;
}

const ExpandedSegmentationImageModal: FC<Props> = ({
  open,
  onClose,
  imageSrc,
  frameIndex,
  allSegmentations,
  visibleSegmentations,
  onSegmentationToggle,
  isSegmentationAvailable,
  segmentationClassOptions,
  getLocalDecision,
  removedReviews,
  onEditSegmentation,
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [frameDimensions, setFrameDimensions] = useState({ width: 0, height: 0 });

  const segmentationClassAcronyms = React.useMemo(
    () =>
      new Map(
        segmentationClassOptions.map(option => [
          option.name.trim().toLowerCase(),
          option.acronym || option.name,
        ])
      ),
    [segmentationClassOptions]
  );

  const hasThumbIndicators = allSegmentations.some(seg => {
    const localD = getLocalDecision ? getLocalDecision(seg.icid) : undefined;
    const wasRemoved = removedReviews ? !!removedReviews[seg.icid] : false;
    return (
      (localD !== undefined && [Decision.ACCEPTED, Decision.REJECTED].includes(localD)) ||
      (!wasRemoved && (!!seg.isApproved || !!seg.isRejected))
    );
  });

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      centered
      width="96vw"
      className="expanded-segmentation-modal"
      style={{ top: 16, maxWidth: '96vw' }}
    >
      <div ref={wrapperRef} className="expanded-segmentation-image-stage">
        <Tooltip title="Close">
          <Button
            shape="circle"
            size="small"
            aria-label="Close expanded image"
            className="expanded-segmentation-close-button"
            icon={<CloseOutlined />}
            onClick={onClose}
          />
        </Tooltip>
        <div className="segmentation-control-stack segmentation-control-stack--modal">
          {allSegmentations.map(seg => {
            const isVisible = visibleSegmentations.has(seg.icid);
            const localD = getLocalDecision ? getLocalDecision(seg.icid) : undefined;
            const wasRemoved = removedReviews ? !!removedReviews[seg.icid] : false;
            const isAccepted =
              localD !== undefined ? localD === Decision.ACCEPTED : !wasRemoved && !!seg.isApproved;
            const isRejected =
              localD !== undefined ? localD === Decision.REJECTED : !wasRemoved && !!seg.isRejected;
            const isAvailable = isSegmentationAvailable(seg);
            const buttonColor = !isAvailable
              ? COLORS.lightDark
              : seg.segmentationColor || undefined;
            const buttonBackground = isVisible ? buttonColor : '#fff';
            const buttonTextColor = isVisible ? '#fff' : buttonColor;
            const segmentationClassFullName = seg.segmentationClassValue?.trim() || 'Segmentation';
            const segmentationClassKey = segmentationClassFullName.toLowerCase();
            const acronym = segmentationClassAcronyms.get(segmentationClassKey);
            const segmentationClassDisplayName = acronym || segmentationClassFullName;

            return (
              <div
                key={seg.icid}
                className="segmentation-control-row segmentation-control-row--modal"
              >
                <Tooltip
                  title={
                    isAvailable
                      ? isVisible
                        ? `Hide ${segmentationClassFullName}`
                        : `Show ${segmentationClassFullName}`
                      : 'Segmentation not available'
                  }
                >
                  <Button
                    size="small"
                    className="segmentation-control-button"
                    disabled={!isAvailable}
                    style={{
                      cursor: !isAvailable ? 'not-allowed' : undefined,
                      opacity: !isAvailable ? 0.45 : 1,
                      backgroundColor: buttonBackground,
                      borderColor: buttonColor,
                      color: buttonTextColor,
                    }}
                    onClick={e => {
                      e.stopPropagation();
                      if (!isAvailable) return;
                      onSegmentationToggle(seg.icid, !isVisible);
                    }}
                  >
                    {segmentationClassDisplayName}
                  </Button>
                </Tooltip>
                <Tooltip
                  title={
                    isRejected
                      ? `Edit ${segmentationClassFullName} in Labeler`
                      : 'You can only edit rejected segmentations'
                  }
                >
                  <Button
                    shape="circle"
                    size="small"
                    type="default"
                    disabled={!isRejected}
                    aria-label={`Edit ${segmentationClassFullName} in Labeler`}
                    icon={<BiMessageSquareEdit style={{ fontSize: 14 }} />}
                    onClick={e => {
                      e.stopPropagation();
                      onEditSegmentation(seg);
                    }}
                  />
                </Tooltip>
                {hasThumbIndicators && (
                  <span className="segmentation-status-slot">
                    {isAccepted && (
                      <Tooltip title="Accepted">
                        <BsHandThumbsUp color="green" style={{ fontSize: 14 }} />
                      </Tooltip>
                    )}
                    {isRejected && (
                      <Tooltip title="Rejected">
                        <BsHandThumbsDown color="red" style={{ fontSize: 14 }} />
                      </Tooltip>
                    )}
                  </span>
                )}
              </div>
            );
          })}
        </div>
        <FrameImage
          src={imageSrc}
          alt={`Expanded frame ${frameIndex}`}
          onLoad={e => {
            const img = e.target as HTMLImageElement;
            if (
              frameDimensions.width !== img.naturalWidth ||
              frameDimensions.height !== img.naturalHeight
            ) {
              setFrameDimensions({ width: img.naturalWidth, height: img.naturalHeight });
            }
          }}
        />
        <SegICIDOverlay
          frameDimensions={frameDimensions}
          allSegmentations={allSegmentations}
          visibleSegmentations={visibleSegmentations}
          wrapperRef={wrapperRef}
          setFrameDimensions={setFrameDimensions}
          stroke={false}
        />
      </div>
    </Modal>
  );
};

export default ExpandedSegmentationImageModal;
